import React, { useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';
import { WorkerDashboard } from '../components/WorkerDashboard';
import { WorkerJobRequests } from '../components/WorkerJobRequests';
import { SettingsScreen } from '../components/SettingsScreen';
import { WorkerIncomingCallScreen } from '../components/WorkerIncomingCallScreen';
import { ChatScreen } from '../components/ChatScreen';
import { LiveTrackingScreen } from '../components/LiveTrackingScreen';
import { UserRole } from '../types';
import { updateJobStatus } from '../services/firestoreService';
import { WorkerLayout } from './WorkerLayout';
import { CMSPage } from '../components/CMSPage';

import { OnboardingScreen } from '../components/OnboardingScreen';

export const WorkerRoutes: React.FC = () => {
    const {
        isLoggedIn, role, userProfile, activeWorkerProfile, setActiveWorkerProfile,
        serviceCategories, feeConfig, myJobs, broadcastJobs, incomingJob, setIncomingJob,
        performLogout, handleProfileUpdate, isOnboarding, setIsOnboarding, handleOnboardingComplete,
        chattingJob, setChattingJob
    } = useSession();

    const [trackingJob, setTrackingJob] = useState<any>(null);

    const navigate = useNavigate();

    // Protected Route Check
    if (!isLoggedIn || role !== UserRole.WORKER) {
        return <Navigate to="/worker/login" />;
    }

    // Show onboarding if needed
    if (isOnboarding) {
        return (
            <OnboardingScreen
                role={role}
                initialPhone={userProfile.phone || ''}
                currentProfile={userProfile}
                onComplete={async (profile, workerDetails) => {
                    await handleOnboardingComplete(profile, workerDetails);
                    setIsOnboarding(false);
                    navigate('/worker');
                }}
            />
        );
    }

    // Interstitials / Overlays (Keep these as overrides for now to preserve behavior)
    if (incomingJob) {
        return (
            <WorkerIncomingCallScreen
                job={incomingJob}
                onAccept={async () => {
                    const jobToAccept = incomingJob;
                    setIncomingJob(null);
                    // ✅ Navigate immediately (optimistic UI)
                    navigate('/worker/task', { state: { tab: 'active' }, replace: true });

                    if (jobToAccept.status === 'OPEN') {
                        if (!activeWorkerProfile) return;
                        try {
                            await updateJobStatus(jobToAccept.id, 'ACCEPTED', {
                                workerId: activeWorkerProfile.id,
                                workerName: activeWorkerProfile.name,
                                workerAvatar: activeWorkerProfile.avatarUrl,
                                workerPhone: activeWorkerProfile.phone
                            });
                        } catch (error: any) {
                            console.error('Accept fail:', error);
                            alert(error.message);
                        }
                    } else {
                        await updateJobStatus(jobToAccept.id, 'ACCEPTED').catch(console.error);
                    }
                }}
                onDecline={async () => {
                    const jobToDecline = incomingJob;
                    setIncomingJob(null);
                    if (jobToDecline.status === 'PENDING') {
                        await updateJobStatus(jobToDecline.id, 'CANCELLED', { cancelledBy: 'worker' }).catch(console.error);
                    }
                }}
            />
        );
    }

    if (chattingJob) {
        return <ChatScreen jobId={chattingJob.id} otherName={chattingJob.customerName || 'Customer'} onBack={() => setChattingJob(null)} perspective="worker" />;
    }

    if (trackingJob) {
        return (
            <LiveTrackingScreen
                job={trackingJob}
                perspective="worker"
                onBack={() => setTrackingJob(null)}
                onChat={() => setChattingJob(trackingJob)}
                onCall={() => {
                    const targetPhone = trackingJob.customerPhone;
                    if (targetPhone && /^\+?[\d\s-]{10,}$/.test(targetPhone)) {
                        window.location.href = `tel:${targetPhone.replace(/[^0-9+]/g, '')}`;
                    } else {
                        alert("Customer phone number not available.");
                    }
                }}
            />
        );
    }

    return (
        <Routes>
            <Route element={<WorkerLayout />}>
                <Route index element={
                    activeWorkerProfile ?
                        <WorkerDashboard defaultTab="stats" categories={serviceCategories} feeConfig={feeConfig} currentWorker={activeWorkerProfile} onUpdateWorker={setActiveWorkerProfile} /> :
                        <div className="p-10 text-center">Loading...</div>
                } />
                <Route path="profile" element={
                    <WorkerDashboard defaultTab="profile" categories={serviceCategories} feeConfig={feeConfig} currentWorker={activeWorkerProfile!} onUpdateWorker={setActiveWorkerProfile} />
                } />
                <Route path="task" element={
                    <WorkerJobRequests
                        availableBroadcasts={activeWorkerProfile ? broadcastJobs.filter(job => job.skill === activeWorkerProfile.skill) : []}
                        activeJobsList={myJobs.filter(j =>
                            (j.status === 'ACCEPTED' || j.status === 'IN_PROGRESS' || j.status === 'COMPLETED' || j.status === 'OTP_VERIFIED') &&
                            (j.workerId === userProfile.phone || j.workerId === userProfile.id)
                        )}
                        onAcceptBroadcast={async (job) => {
                            if (job.status === 'OPEN') {
                                if (!activeWorkerProfile) {
                                    alert("Worker profile not ready.");
                                    return;
                                }
                                await updateJobStatus(job.id, 'ACCEPTED', {
                                    workerId: activeWorkerProfile?.id || userProfile.id,
                                    workerName: activeWorkerProfile?.name || `${userProfile.firstName} ${userProfile.lastName}`,
                                    workerAvatar: activeWorkerProfile?.avatarUrl || userProfile.avatarUrl,
                                    workerPhone: activeWorkerProfile?.phone || userProfile.phone
                                });
                            }
                            // Navigate logic usually not needed if list updates
                        }}
                        onChat={setChattingJob}
                        onTrackJob={setTrackingJob}
                        onCancelJob={(jobId) => updateJobStatus(jobId, 'CANCELLED', { cancelledBy: 'worker' })}
                        feeConfig={feeConfig}
                        workerUpiId={activeWorkerProfile?.upiId}
                    />
                } />
                <Route path="settings" element={
                    <SettingsScreen
                        role={role}
                        onLogout={performLogout}
                        userProfile={userProfile}
                        onUpdateProfile={handleProfileUpdate}
                        workerProfile={activeWorkerProfile || undefined} // Pass worker profile
                        onUpdateWorker={setActiveWorkerProfile} // Allow updates
                    />
                } />

                <Route path="privacy-policy" element={<CMSPage slug="privacy" title="Privacy Policy" />} />
                <Route path="help" element={<CMSPage slug="help" title="Help Center" />} />
                <Route path="terms-of-services" element={<CMSPage slug="terms" title="Terms of Service" />} />
            </Route>
        </Routes>
    );
};
